/**
 * Text transforms for the composer's formatting controls.
 *
 * Kept out of the component so the fiddly parts — where the caret lands after
 * a wrap, what happens when nothing is selected — are testable and are not
 * re-derived in three places.
 */

export interface TextEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/**
 * Wraps the selection in `before`/`after`, or unwraps it when it is already
 * wrapped. With nothing selected it inserts the pair and puts the caret
 * between them, so `Cmd+B` then typing produces bold text.
 */
export function toggleWrap(
  value: string,
  start: number,
  end: number,
  before: string,
  after = before,
): TextEdit {
  const selected = value.slice(start, end);

  // Already wrapped, inside the selection: unwrap.
  if (
    selected.startsWith(before) &&
    selected.endsWith(after) &&
    selected.length >= before.length + after.length
  ) {
    const inner = selected.slice(before.length, selected.length - after.length);
    return {
      value: value.slice(0, start) + inner + value.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }

  // Already wrapped, just outside the selection: unwrap that instead.
  const outerStart = start - before.length;
  const outerEnd = end + after.length;
  if (
    outerStart >= 0 &&
    value.slice(outerStart, start) === before &&
    value.slice(end, outerEnd) === after
  ) {
    return {
      value: value.slice(0, outerStart) + selected + value.slice(outerEnd),
      selectionStart: outerStart,
      selectionEnd: outerStart + selected.length,
    };
  }

  return {
    value: value.slice(0, start) + before + selected + after + value.slice(end),
    selectionStart: start + before.length,
    selectionEnd: start + before.length + selected.length,
  };
}

/** Prefixes every selected line, or strips the prefix if all lines have it. */
export function toggleLinePrefix(
  value: string,
  start: number,
  end: number,
  prefix: string | ((index: number) => string),
): TextEdit {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineEndIndex = value.indexOf('\n', end);
  const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;

  const block = value.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const at = (index: number) => (typeof prefix === 'string' ? prefix : prefix(index));

  const allPrefixed = lines.every((line, index) => line.startsWith(at(index)));
  const next = lines
    .map((line, index) =>
      allPrefixed ? line.slice(at(index).length) : `${at(index)}${line}`,
    )
    .join('\n');

  return {
    value: value.slice(0, lineStart) + next + value.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + next.length,
  };
}

/** Wraps the selection in a fenced block on its own lines. */
export function wrapCodeFence(value: string, start: number, end: number): TextEdit {
  const selected = value.slice(start, end);
  const needsLeadingBreak = start > 0 && value[start - 1] !== '\n';
  const lead = needsLeadingBreak ? '\n' : '';
  const inserted = `${lead}\`\`\`\n${selected}\n\`\`\`\n`;
  return {
    value: value.slice(0, start) + inserted + value.slice(end),
    // Caret goes to the language slot, which is what you type next.
    selectionStart: start + lead.length + 3,
    selectionEnd: start + lead.length + 3,
  };
}

/** Turns the selection into a link, or inserts an empty link template. */
export function insertLink(value: string, start: number, end: number): TextEdit {
  const selected = value.slice(start, end);
  const looksLikeUrl = /^https?:\/\/\S+$/i.test(selected);
  const label = looksLikeUrl ? '' : selected;
  const href = looksLikeUrl ? selected : '';
  const inserted = `[${label}](${href})`;
  // Caret lands in whichever half is still empty.
  const caret = looksLikeUrl ? start + 1 : start + label.length + 3;
  return {
    value: value.slice(0, start) + inserted + value.slice(end),
    selectionStart: caret,
    selectionEnd: caret,
  };
}

/* ------------------------------ mentions --------------------------------- */

/**
 * Re-exported rather than defined here: mobile needs the same round-trip, and
 * two copies of "what counts as a mention" drift. The implementation lives in
 * `@itsappy/shared` beside the parser that has to agree with it.
 */
export { toComposerText, toMessageBody } from '@itsappy/shared';

/* ------------------------------- drafts ---------------------------------- */

const DRAFT_PREFIX = 'itsappy.draft.';

/**
 * Drafts survive a refresh, a navigation and a crash.
 *
 * Per channel and per browser rather than server-side: a half-written message
 * is not something to sync to other devices, and losing one to a reload is the
 * kind of small betrayal people remember.
 */
export function readDraft(channelId: string): string {
  try {
    return localStorage.getItem(DRAFT_PREFIX + channelId) ?? '';
  } catch {
    return '';
  }
}

export function writeDraft(channelId: string, value: string): void {
  try {
    if (value.trim()) localStorage.setItem(DRAFT_PREFIX + channelId, value);
    else localStorage.removeItem(DRAFT_PREFIX + channelId);
  } catch {
    // A full or blocked localStorage must not stop someone typing.
  }
}
